import React, { useState, useEffect } from 'react';
import NavbarRolServicios from '../navbarRolServicios';
import { Button, Input, Typography, message } from "antd";
import { SearchOutlined } from '@ant-design/icons';
import { utils, writeFile } from 'xlsx';
import axios from 'axios';
import './pagosRolServicios.css';

const PagosRolServicios = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // New state variables for custom table
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'descending' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get('https://99envios.app/api/online/transaccion');
      const sortedData = response.data.map(item => ({ ...item, key: item.id })).sort((a, b) => b.id - a.id);
      setData(sortedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to fetch data');
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(data);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Data");
    writeFile(workbook, "pagos_data.xlsx");
  };

  const handleUpdateSelected = async () => {
    if (selectedRows.length === 0) {
      message.warning('Por favor seleccione al menos un registro para actualizar');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Update each selected row sequentially
      for (const id of selectedRows) {
        try {
          await axios.put(`https://99envios.app/api/online/transaccion/${id}`);
          successCount++;
        } catch (error) {
          console.error(`Error updating ID ${id}:`, error);
          errorCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        message.success(`${successCount} registros actualizados exitosamente`);
      }
      if (errorCount > 0) {
        message.error(`${errorCount} registros fallaron al actualizar`);
      }

      // Refresh data and clear selection
      await fetchData();
      setSelectedRows([]);
    } catch (error) {
      console.error('Error en actualización masiva:', error);
      message.error('Error al realizar la actualización masiva');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectSelected = async () => {
    if (selectedRows.length === 0) {
      message.warning('Por favor seleccione al menos un registro para rechazar');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Reject each selected row sequentially
      for (const id of selectedRows) {
        try {
          await axios.post(`https://99envios.app/api/online/rechazar-pago/${id}`);
          successCount++;
        } catch (error) {
          console.error(`Error rejecting ID ${id}:`, error);
          errorCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        message.success(`${successCount} registros rechazados exitosamente`);
      }
      if (errorCount > 0) {
        message.error(`${errorCount} registros fallaron al rechazar`);
      }

      // Refresh data and clear selection
      await fetchData();
      setSelectedRows([]);
    } catch (error) {
      console.error('Error en rechazo masivo:', error);
      message.error('Error al realizar el rechazo masivo');
    } finally {
      setLoading(false);
    }
  };
  
  // New functions for custom table
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      if (a[sortConfig.key] === null) return 1;
      if (b[sortConfig.key] === null) return -1;
      
      if (typeof a[sortConfig.key] === 'string') {
        if (sortConfig.direction === 'ascending') {
          return a[sortConfig.key].localeCompare(b[sortConfig.key]);
        } else {
          return b[sortConfig.key].localeCompare(a[sortConfig.key]);
        }
      } else {
        if (sortConfig.direction === 'ascending') {
          return a[sortConfig.key] - b[sortConfig.key];
        } else {
          return b[sortConfig.key] - a[sortConfig.key];
        }
      }
    });
  };

  const filterData = (data) => {
    if (!searchTerm) return data;
    
    return data.filter(item => 
      Object.values(item).some(val => 
        val && val.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const handleUpdateStatus = async (id) => {
    try {
      setLoading(true);
      const response = await axios.put(`https://99envios.app/api/online/transaccion/${id}`);
      
      if (response.status === 200) {
        message.success(`Estado actualizado con éxito para el ID: ${id}`);
        await fetchData();
      } else {
        message.error(`Error al actualizar el estado para el ID: ${id}`);
      }
    } catch (error) {
      console.error('Error al actualizar el estado:', error);
      message.error(`Error al actualizar el estado para el ID: ${id}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectStatus = async (id) => {
    try {
      setLoading(true);
      const response = await axios.post(`https://99envios.app/api/online/rechazar-pago/${id}`);

      if (response.status === 200) {
        message.success(`Estado rechazado con éxito para el ID: ${id}`);
        await fetchData();
      } else {
        message.error(`Error al rechazar el estado para el ID: ${id}`);
      }
    } catch (error) {
      console.error('Error al rechazar el estado:', error);
      message.error(`Error al rechazar el estado para el ID: ${id}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle checkbox selection
  const handleRowSelect = (id) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelectedRows = [...selectedRows];
    
    if (selectedIndex === -1) {
      newSelectedRows.push(id);
    } else {
      newSelectedRows.splice(selectedIndex, 1);
    }
    
    setSelectedRows(newSelectedRows);
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageIds = currentItems.map(item => item.id);
      setSelectedRows([...new Set([...selectedRows, ...currentPageIds])]);
    } else {
      // Only deselect items on current page
      const currentPageIds = new Set(currentItems.map(item => item.id));
      setSelectedRows(selectedRows.filter(id => !currentPageIds.has(id)));
    }
  };

  // Get filtered and sorted data
  const filteredAndSortedData = sortData(filterData(data));
  
  // Calculate pagination values
  const indexOfLastItem = currentPage * entriesPerPage;
  const indexOfFirstItem = indexOfLastItem - entriesPerPage;
  const currentItems = filteredAndSortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSortedData.length / entriesPerPage);
  
  // Function to change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Get formatted value for monto
  const getFormattedMonto = (monto) => {
    return parseFloat(monto).toLocaleString('es-CO', {
      style: 'currency',
      currency: 'COP',
    });
  };
  
  return (
    <>
      <NavbarRolServicios title={"Pagos"} />
      <div className="transaccion-pagos-container">
        <Typography.Title level={2} className="transaccion-pagos-title">
          Pagos
        </Typography.Title>
        <div className="transaccion-pagos-actions">
          <Button 
            onClick={exportToExcel} 
            style={{ height: 46 }}
            className="transaccion-pagos-button transaccion-pagos-export-button"
          >
            Exportar a Excel
          </Button>
          <Button
            onClick={handleUpdateSelected}
            loading={loading}
            disabled={selectedRows.length === 0}
            style={{ height: 48 }}
            type="primary"
            className="transaccion-pagos-button"
          >
            Actualizar Seleccionados ({selectedRows.length})
          </Button>
          <Button
            onClick={handleRejectSelected}
            loading={loading}
            disabled={selectedRows.length === 0}
            style={{ height: 48 }}
            type="danger"
            className="transaccion-pagos-button"
          >
            Rechazar Seleccionados ({selectedRows.length})
          </Button>
        </div>
        
        <div className="transaccion-pagos-search">
          <Input
            placeholder="Buscar en todos los campos"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            prefix={<SearchOutlined />}
            className="transaccion-pagos-search-input"
          />
        </div>
        
        <Typography.Title level={3} className="transaccion-pagos-subtitle">
          Tabla Pagos
        </Typography.Title>

        {loading ? (
          <div className="transaccion-pagos-loading">Cargando datos...</div>
        ) : (
          <div className="transaccion-pagos-table-wrapper">
            <table className="transaccion-pagos-custom-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={currentItems.length > 0 && currentItems.every(item => selectedRows.includes(item.id))}
                    />
                  </th>
                  <th onClick={() => handleSort('id')}>
                    Registro {sortConfig.key === 'id' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('codigo_inter')}>
                    Código Inter {sortConfig.key === 'codigo_inter' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('id_sucursal')}>
                    Código Sucursal {sortConfig.key === 'id_sucursal' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('nombre')}>
                    Nombre {sortConfig.key === 'nombre' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('tipo_identificacion')}>
                    Tipo ID {sortConfig.key === 'tipo_identificacion' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('numero_identificacion')}>
                    Número ID {sortConfig.key === 'numero_identificacion' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('tipo_transaccion')}>
                    Tipo Transacción {sortConfig.key === 'tipo_transaccion' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('tipo_cuenta')}>
                    Tipo Cuenta {sortConfig.key === 'tipo_cuenta' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('nombre_banco')}>
                    Nombre Banco {sortConfig.key === 'nombre_banco' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('codigo_banco')}>
                    Código Banco {sortConfig.key === 'codigo_banco' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('numero_cuenta')}>
                    Número Cuenta {sortConfig.key === 'numero_cuenta' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('Fecha')}>
                    Fecha {sortConfig.key === 'Fecha' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('Monto')}>
                    Monto {sortConfig.key === 'Monto' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('estado')}>
                    Estado {sortConfig.key === 'estado' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('fecha_pago')}>
                    Fecha Pago/Rechazo {sortConfig.key === 'fecha_pago' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id} className={selectedRows.includes(item.id) ? 'selected-row' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item.id)}
                        onChange={() => handleRowSelect(item.id)}
                      />
                    </td>
                    <td>{item.id}</td>
                    <td>{item.codigo_inter}</td>
                    <td>{item.id_sucursal}</td>
                    <td>{item.nombre}</td>
                    <td>{item.tipo_identificacion}</td>
                    <td>{item.numero_identificacion}</td>
                    <td>{item.tipo_transaccion}</td>
                    <td>{item.tipo_cuenta}</td>
                    <td>{item.nombre_banco}</td>
                    <td>{item.codigo_banco}</td>
                    <td>{item.numero_cuenta}</td>
                    <td>{item.Fecha}</td>
                    <td>{getFormattedMonto(item.Monto)}</td>
                    <td>{item.estado}</td>
                    <td>{item.fecha_pago || 'N/A'}</td>
                    <td>
                      <div className="action-buttons-pagos-rol-cartera">
                        <button 
                          onClick={() => handleUpdateStatus(item.id)} 
                          disabled={loading}
                          className="actualizar-button-pagos-rol-cartera"
                        >
                          {loading ? 'Cargando...' : 'Actualizar'}
                        </button>
                        <button 
                          onClick={() => handleRejectStatus(item.id)} 
                          disabled={loading}
                          className="rechazar-button-pagos-rol-cartera"
                        >
                          {loading ? 'Cargando...' : 'Rechazar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination controls */}
        {filteredAndSortedData.length > 0 && (
          <div className="pagination-container-pagos-rol-cartera">
            <div className="pagination-info-pagos-rol-cartera">
              Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredAndSortedData.length)} de {filteredAndSortedData.length} registros
            </div>
            <div className="pagination-controls-pagos-rol-cartera">
              {/* First page button */}
              {currentPage > 2 && (
                <button 
                  onClick={() => paginate(1)} 
                  className="pagination-button-pagos-rol-cartera"
                >
                  1
                </button>
              )}
              
              {/* Previous button */}
              {currentPage > 1 && (
                <button 
                  onClick={() => paginate(currentPage - 1)} 
                  className="pagination-button-pagos-rol-cartera"
                >
                  &lt;
                </button>
              )}
              
              {/* Page numbers */}
              <div className="pagination-pages-pagos-rol-cartera">
                {(() => {
                  const pagesToShow = [];
                  
                  if (currentPage > 1) {
                    pagesToShow.push(currentPage - 1);
                  }
                  
                  pagesToShow.push(currentPage);
                  
                  if (currentPage < totalPages) {
                    pagesToShow.push(currentPage + 1);
                  }
                  
                  return pagesToShow.map(pageNum => (
                    <button 
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`pagination-button-pagos-rol-cartera ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  ));
                })()}
              </div>
              
              {/* Next button */}
              {currentPage < totalPages && (
                <button 
                  onClick={() => paginate(currentPage + 1)} 
                  className="pagination-button-pagos-rol-cartera"
                >
                  &gt;
                </button>
              )}
              
              {/* Last page button */}
              {currentPage < totalPages - 1 && (
                <button 
                  onClick={() => paginate(totalPages)} 
                  className="pagination-button-pagos-rol-cartera"
                >
                  {totalPages}
                </button>
              )}
            </div>
            <div className="items-per-page-pagos-rol-cartera">
              <span>Registros por página: </span>
              <select 
                value={entriesPerPage} 
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PagosRolServicios;